// deps
import React, { ReactElement, useMemo } from "react";

// context
import { UpdateCartContext, UpdateCartContextValue } from "@cart-client/contexts/UpdateCartContext";
import { CouponListingContext } from "@cart-client/contexts/CouponListingContext";
import { MROFoodCartContextProvider } from "@cart-client/contexts/MROFoodCartContextProvider";

// hooks
import { usePerformMROFoodCartUpdate } from "@cart-client/hooks/usePerformMROFoodCartUpdate";
import useMROFoodCartApplyCoupon from "@cart-client/hooks/useMROFoodCartApplyCoupon";

/**
 * Action provider for the MRO (Multi-Restaurant Order) cart.
 *
 * Implements the SAME `UpdateCartContext` and `CouponListingContext` interfaces as
 * `CartActionProvider` / `GiftingCartActionProvider`, so existing low-level
 * components consume cart actions without any change to their calling interface.
 *
 * MRO-specific behaviour that does not fit the shared (single-restaurant) interface
 * — namely coupon apply/remove scoped to a child cart, and the active restaurant
 * tab — is exposed via the additive `MROFoodCartContext` instead of bending the
 * shared contracts
 */
export function MROFoodCartActionProvider(props: { children: React.ReactNode }): ReactElement {
    // cart operations
    const [{ updateCartInProgress, updateCartData, updateCartError }, doUpdateCart, reloadCart] =
        usePerformMROFoodCartUpdate();

    // coupon operations
    const {
        toggleCouponModal,
        showCouponModal,
        updateCouponState,
        togglingDelightPopup,
        showDelightPopup,
        syncApplyCouponCart,
        resetCouponListingPage,
        fetchCoupons,
        isFetching,
        isApplyingCoupon,
        isRemovingCoupon,
        coupons,
        nextBestCoupon,
        errorMessage,
        clearErrorMessage,
        cards,
        hasInitiallyFetched,
        hasApplyCouponInitiated,
        handleCouponListingPageCouponRemove,
        showPendingDiscountingComms,
        lastAppliedCouponRestaurantId,
        applyCouponClashPopup,
    } = useMROFoodCartApplyCoupon();

    const updateCartContextValue: UpdateCartContextValue = useMemo(
        () => ({
            updateCartInProgress: updateCartInProgress || updateCouponState,
            updateCartData,
            updateCartError,
            doUpdateCart,
            reloadCart,
            toggleCouponModal,
            showCouponModal,
            syncApplyCouponCart,
            resetCouponListingPage,
            showDelightPopup,
            togglingDelightPopup,
            handleCouponListingPageCouponRemove,
            showPendingDiscountingComms,
            lastAppliedCouponRestaurantId,
            setShowM2ChalfcardOnClick: () => {
                // No-op for MRO.
            },
        }),
        [
            updateCartInProgress,
            updateCouponState,
            updateCartData,
            updateCartError,
            doUpdateCart,
            reloadCart,
            toggleCouponModal,
            showCouponModal,
            syncApplyCouponCart,
            resetCouponListingPage,
            showDelightPopup,
            togglingDelightPopup,
            handleCouponListingPageCouponRemove,
            showPendingDiscountingComms,
            lastAppliedCouponRestaurantId,
        ],
    );

    const couponListingContextValue = useMemo(
        () => ({
            fetchCoupons,
            isFetching,
            isApplyingCoupon,
            isRemovingCoupon,
            coupons,
            nextBestCoupon,
            errorMessage,
            clearErrorMessage,
            resetCouponListingPage,
            cards,
            hasInitiallyFetched,
            hasApplyCouponInitiated,
            applyCouponClashPopup,
        }),
        [
            fetchCoupons,
            isFetching,
            isApplyingCoupon,
            isRemovingCoupon,
            coupons,
            nextBestCoupon,
            errorMessage,
            clearErrorMessage,
            resetCouponListingPage,
            cards,
            hasInitiallyFetched,
            hasApplyCouponInitiated,
            applyCouponClashPopup,
        ],
    );
    return (
        <UpdateCartContext.Provider value={updateCartContextValue}>
            <CouponListingContext.Provider value={couponListingContextValue}>
                <MROFoodCartContextProvider>{props.children}</MROFoodCartContextProvider>
            </CouponListingContext.Provider>
        </UpdateCartContext.Provider>
    );
}
